import React, { useCallback, useContext, useEffect, useState } from "react";
import { Grid, Text, Card } from "@pushpress/shared-components";
import * as Ably from "ably";
import {
  AblyProvider,
  ChannelProvider,
  useChannel,
  useChannelStateListener,
} from "ably/react";
import {
  Badge,
  BadgesService,
  BadgeTemplate,
} from "@pushpress/committed-club-typescript-sdk";
import BadgeListItem from "./BadgeListItem";
import CommitedClubAboutPanel from "./CommitedClubAboutPanel";
import {
  getCommittedClubCheckinChannelName,
  getAblyApiKey,
  showErrorToast,
} from "./utils";
import { AppContext } from "../../context/app";
import CommittedClubAblyProvider from "./CommittedClubAblyProvider";

type CommittedClubPostLaunchProps = {
  badgeTemplate?: BadgeTemplate;
};

const client = new Ably.Realtime({
  key: getAblyApiKey(),
});

const CommittedClubPostLaunchWrapper = (
  props: CommittedClubPostLaunchProps,
) => {
  const { badgeTemplate } = props;
  const app = useContext(AppContext);
  return (
    <AblyProvider client={client}>
      <ChannelProvider
        channelName={getCommittedClubCheckinChannelName(app.client.uuid)}
      >
        <CommittedClubPostLaunch badgeTemplate={badgeTemplate} />
      </ChannelProvider>
    </AblyProvider>
  );
};

const CommittedClubPostLaunch = (props: CommittedClubPostLaunchProps) => {
  const { badgeTemplate } = props;
  const [badges, setBadges] = useState<Badge[]>([]);

  const app = useContext(AppContext);

  const fetchBadges = useCallback(async () => {
    const response = await BadgesService.getBadges();
    if (response.error) {
      showErrorToast("Failed to load club progress");
    } else {
      setBadges(response.data);
    }
  }, []);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  useChannelStateListener(
    getCommittedClubCheckinChannelName(app.client.uuid),
    (stateChange) => {
      // Potentially redundant check to ensure we didn't miss a checkin event
      // as we were connecting to Ably
      if (stateChange.current === "attached") {
        fetchBadges();
      }
    },
  );

  useChannel(getCommittedClubCheckinChannelName(app.client.uuid), (message) => {
    const eventName = `app.checkin:${app.client.uuid}`;
    if (message.name === eventName) {
      fetchBadges();
    }
  });

  const content = (
    <Grid item xs={12} md={8} alignItems="flex-start">
      <Card>
        <Card.Content>
          <div className="tw-py-4">
            <Text weight="600" variant="heading-3" color="black">
              {badgeTemplate.title}
            </Text>
            <div className="tw-h-6" />
            {badges.map((badge) => (
              <>
                <BadgeListItem badge={badge} />
                <div className="tw-h-4" />
              </>
            ))}
            <div className="tw-h-4" />
          </div>
        </Card.Content>
      </Card>
    </Grid>
  );

  return (
    <>
      <div className="tw-h-1" />
      <Grid
        container
        justifyContent="space-between"
        spacing={6}
        columnSpacing={6}
      >
        {content}
        <CommitedClubAboutPanel hasLaunched isClubSetup />
      </Grid>
    </>
  );
};

export default CommittedClubPostLaunchWrapper;
